/**
 * Typescript port of: [vendor/magento/module-quote-graph-ql/etc/schema.graphqls]
 */

import { MagentoCountryCodeEnum } from "./localization";

/**
 *
 */
export interface CartAddressRegion {
  code?: string;
  label?: string;
  region_id?: number;
}

export interface CartAddressCountry {
  code: string;
  label: string;
}

export interface CartAddressInterface {
  firstname: string;
  lastname: string;
  company?: string;
  street: string[];
  city: string;
  region: CartAddressRegion;
  postcode: string;
  country: CartAddressCountry;
  telephone: string;
}

export interface BillingCartAddress extends CartAddressInterface {}

export interface Money {
  value: number /* Float */;

  /**
   * A three-letter currency code, such as USD or EUR
   */
  currency: string;
}

export interface AvailableShippingMethod {
  carrier_code: string;
  carrier_title: string;
  method_code?: string;
  method_title?: string;
  error_message?: string;
  amount: Money;
  base_amount?: Money;
  price_excl_tax: Money;
  price_incl_tax: Money;
  available: boolean;
}

export interface SelectedShippingMethod {
  carrier_code: string;
  method_code: string;
  carrier_title: string;
  method_title: string;
  amount: Money;
  base_amount?: Money;
}

export interface ShippingCartAddress extends CartAddressInterface {
  available_shipping_methods?: AvailableShippingMethod[];
  selected_shipping_method?: SelectedShippingMethod;
}

export type ID = string;

export interface Discount {
  amount: Money;
  label: string;
}

export interface CartItemPrices {
  price: Money;
  row_total: Money;
  row_total_including_tax: Money;
  discounts?: Discount[];
  total_item_discount?: Money;
}

export interface ComplexTextValue {
  html: string;
}

export interface MediaGalleryInterface {
  url?: string;
  label?: string;
  position?: number /* int */;
  disabled?: boolean;
}

export interface ProductMediaGalleryEntriesContent {
  base64_encoded_data?: string;
  type?: string;
  name?: string;
}

export interface ProductMediaGalleryEntriesVideoContent {
  media_type?: string;
  video_provider?: string;
  video_url?: string;
  video_title?: string;
  video_description?: string;
  video_metadata?: string;
}

export interface MediaGalleryEntry {
  id?: number /* Int */;
  uid: ID;
  media_type?: string;
  label?: string;
  position?: number /* Int */;
  disabled?: boolean;
  types?: string[];
  file?: string;
  content?: ProductMediaGalleryEntriesContent;
  video_content?: ProductMediaGalleryEntriesVideoContent;
}

export interface ProductImage extends MediaGalleryInterface {}

export interface Website {
  id?: number /* Int */;
  name?: string;
  code?: string;
  sort_order?: number /* Int */;
  default_group_id?: string;
  is_default?: boolean;
}

export interface ProductLinksInterface {
  sku?: string;
  link_type?: string;
  linked_product_sku?: string;
  linked_product_type?: string;
  position?: number /* Int */;
}

export type PriceAdjustmentCodesEnum = "TAX" | "WEEE" | "WEEE_TAX";

export type PriceAdjustmentDescriptionEnum = "INCLUDED" | "EXCLUDED";

export interface PriceAdjustment {
  amount?: Money;
  code?: PriceAdjustmentCodesEnum;
  description?: PriceAdjustmentDescriptionEnum;
}

export interface Price {
  amount?: Money;
  adjustments?: PriceAdjustment[];
}

export interface ProductPrices {
  minimalPrice?: Price;
  maximalPrice?: Price;
  regularPrice?: Price;
}

export interface ProductDiscount {
  percent_off?: number /* Float */;
  amount_off?: number /* Float */;
}

export interface ProductPrice {
  regular_price: Money;
  final_price: Money;
  discount?: ProductDiscount;
}

export interface PriceRange {
  minimum_price: ProductPrice;
  maximum_price?: ProductPrice;
}

export interface SearchResultPageInfo {
  page_size?: number /* Int */;
  current_page?: number /* Int */;
  total_pages?: number /* Int */;
}

export interface CategoryProducts {
  items?: ProductInterface[];
  page_info?: SearchResultPageInfo;
  total_count?: number /* Int */;
}

export interface Breadcrumb {
  category_id?: number /* Int */;
  category_uid: ID;
  category_name?: string;
  category_level?: number /* Int */;
  category_url_key?: string;
  category_url_path?: string;
}

export interface CmsBlock {
  identifier?: string;
  title?: string;
  content?: string;
}

export interface CategoryInterface {
  id?: number /* Int */;
  uid: ID;
  description?: string;
  name?: string;
  path?: string;
  path_in_store?: string;
  url_key?: string;
  url_path?: string;
  canonical_url?: string;
  position?: number /* Int */;
  level?: number /* Int */;
  created_at?: string;
  updated_at?: string;
  product_count?: number /* Int */;
  default_sort_by?: string;
  products?: CategoryProducts;
  breadcrumbs?: Breadcrumb[];

  cms_block?: CmsBlock;
  staged?: boolean;
  url_suffix?: string;
}

export interface ProductTierPrices {
  customer_group_id?: string;
  qty?: number /* Float */;
  value?: number /* Float */;
  percentage_value?: number /* Float */;
  website_id?: number /* Float */;
}

export interface TierPrice {
  final_price?: Money;
  quantity?: number /* Float */;
  discount?: ProductDiscount;
}

export interface ProductInterface {
  id: number /* int */;
  uid: ID;
  name?: string;
  sku?: string;
  description?: ComplexTextValue;
  short_description?: ComplexTextValue;
  special_price?: number /* Float */;
  special_from_date?: string;
  special_to_date?: string;
  attribute_set_id?: number /* int */;
  meta_title?: string;
  meta_keyword?: string;
  meta_description?: string;
  image?: ProductImage;
  small_image?: ProductImage;
  thumbnail?: ProductImage;
  new_from_date?: string;
  new_to_date?: string;
  tier_price?: number /* Float */;
  options_container?: string;
  created_at?: string;
  updated_at?: string;
  country_of_manufacture?: string;
  type_id?: string;
  websites?: Website[];
  product_links?: ProductLinksInterface[];
  media_gallery_entries?: MediaGalleryEntry[];
  price?: ProductPrices;
  price_range: PriceRange;
  gift_message_available?: string;
  manufacturer?: number /* Int */;
  categories?: CategoryInterface[];
  canonical_url?: string;
  media_gallery?: MediaGalleryInterface[];

  tier_prices?: ProductTierPrices[];
  tiered_pricing_available?: boolean;
  price_tiers?: TierPrice[];
  potential_price_tiers?: TierPrice[];

  cart_description?: string;
  item_list_desc?: string;

  url_key?: string;
  url_prefix?: string;
  url_rewrites: UrlRewrite[];
}
export interface UrlRewrite {
  url?: string;
  parameters?: HttpQueryParameter[];
}
export interface HttpQueryParameter {
  name?: string;
  value?: string;
}

export interface GroupedProduct extends ProductInterface {
  items?: GroupedProductItem[];
}
export interface GroupedProductItem {
  /**
   * Float
   */
  qty?: number;
  /**
   * Int
   */
  position?: number;
  product?: ProductInterface;
}

export const isGroupedProduct = (
  product: ProductInterface | GroupedProduct
): product is GroupedProduct => {
  return !!(product as GroupedProduct).items;
};

export interface ProductInterface_PricingInformation {
  productHasMemberPrice?: boolean;
  productMemberPrice?: number;
  productHasSalesPrice?: boolean;
  productPriceAfter?: number;
  productPriceBefore?: number;
}
export const extractPricingInformationFromProductInterface = (
  product: ProductInterface
): ProductInterface_PricingInformation => {
  const memberDiscount = product.price_tiers ? product.price_tiers[0] : null;
  const saleDiscount = product.price_range
    ? product.price_range.minimum_price
    : null;
  const noDiscount = product.price_range
    ? product.price_range.maximum_price
    : null;

  const noDiscountPrice = noDiscount ? noDiscount.final_price.value : 0;
  const memberDiscountedPrice = memberDiscount
    ? memberDiscount.final_price?.value || 0
    : 0;
  let saleDiscountPrice = 0;
  if (
    saleDiscount &&
    saleDiscount.regular_price &&
    saleDiscount.regular_price.value > 0 &&
    saleDiscount.regular_price.value > saleDiscount.final_price.value
  ) {
    saleDiscountPrice = saleDiscount.final_price.value;
  }

  let productPriceAfter;
  let productPriceBefore;
  let isMemberPricing = false;
  let isSalePricing = false;
  if (
    memberDiscountedPrice !== 0 &&
    memberDiscountedPrice <= saleDiscountPrice
  ) {
    productPriceBefore =
      (memberDiscount?.final_price?.value || 0) +
      (memberDiscount?.discount?.amount_off || 0);
    productPriceAfter = memberDiscountedPrice;
    isMemberPricing = true;
  } else if (saleDiscountPrice !== 0) {
    productPriceBefore = saleDiscount?.regular_price.value || 0;
    productPriceAfter = saleDiscountPrice;
    isSalePricing = true;
  } else {
    productPriceAfter = noDiscountPrice;
  }

  const productPricingInformation: ProductInterface_PricingInformation = {
    productHasMemberPrice: isMemberPricing,
    productMemberPrice: productPriceAfter,
    productHasSalesPrice: isSalePricing,
    productPriceAfter,
    productPriceBefore,
  };

  return productPricingInformation;
};

export enum BbntStockStatus {
  AVAILABLE = "Available",
  IN_STORE_ONLY = "Out of stock online",
  OUT_OF_STOCK = "Out Of Stock",
  LIMITED_STOCK = "Hurry - Limited stock left!",

  SOLD_OUT_ONLINE = "Sold out Online",
  COMING_SOON = "Coming Soon",

  LIMITED_STOCK_ORIGINAL = "Limited Stock",
  IN_STORE_ONLY_ORIGINAL = "In Store Only",
}
export interface CartItemInterface {
  id: string;
  uid: ID;
  quantity: number /* float */;
  prices?: CartItemPrices;
  product: ProductInterface;

  bbnt_product_url?: string;
  bbnt_stock_status?: `${BbntStockStatus}`;
  giftcard_attributes?: CartItemInterface_GiftcardCustomData;
}

export interface CartItemInterface_GiftcardCustomData {
  giftcard_sender_name: string;
  giftcard_sender_email: string;
  giftcard_recipient_name: string;
  giftcard_recipient_email: string;
  giftcard_message: string;
}

export interface AppliedCoupon {
  code: string;
  label: string;
}

export interface AppliedGiftCard {
  code?: string;
  expiration_date?: string;
  current_balance?: Money;
  applied_balance?: Money;
}

export interface CartDiscount {
  amount: Money;
  label: String[];
}

export interface CartTaxItem {
  amount: Money;
  label: string;
}

export interface CartPrices {
  grand_total?: Money;
  subtotal_including_tax?: Money;
  subtotal_excluding_tax?: Money;
  discount?: CartDiscount;
  subtotal_with_discount_excluding_tax?: Money;
  applied_taxes?: CartTaxItem[];
  discounts?: Discount[];
}

export interface Cart {
  id: ID;
  email?: string;
  billing_address?: BillingCartAddress;
  shipping_addresses?: ShippingCartAddress[];
  items?: CartItemInterface[];
  applied_coupons?: AppliedCoupon[];
  applied_gift_cards?: [AppliedGiftCard];
  prices?: CartPrices;

  store_code?: string;
}

export interface SetShippingAddressesOnCartInput {
  cart_id: string;
  shipping_addresses: ShippingAddressInput[];
}
export interface ShippingAddressInput {
  customer_address_id?: number;
  address: CartAddressInput;
  customer_notes?: string;
}
export interface SetBillingAddressOnCartInput {
  cart_id: string;
  billing_address: BillingAddressInput;
}
export interface BillingAddressInput {
  customer_address_id?: number;
  address: CartAddressInput;
  same_as_shipping?: boolean;
}
export interface CartAddressInput {
  firstname: string;
  lastname: string;
  company?: string;
  street: string[];
  city: string;
  region?: string;
  region_id?: number;
  postcode?: string;
  country_code: string;
  telephone: string;
  save_in_address_book?: boolean;
}

export interface SetShippingMethodsOnCartInput {
  cart_id: string;
  shipping_methods: ShippingMethodInput[];
}
export interface ShippingMethodInput {
  carrier_code: string;
  method_code: string;
}

export interface LegacyMagento_ShippingInformation_CartAddressInput {
  email: string;

  country_id: `${MagentoCountryCodeEnum}`;

  firstname: string;
  lastname: string;
  street: string[];
  city: string;
  region?: string;
  region_id?: number;
  postcode?: string;
  telephone: string;
}
